import {
    Directive,
    EventEmitter,
    HostListener,
    Input,
    Output,
} from '@angular/core';
import { interval, Subscription, timer } from 'rxjs';
import { mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
    selector: '[longPress]',
    standalone: true
})
export class LongPressDirective {
    @Input('longPress')
    set duration(v: number | string) {
        this._duration = v ? Number(v) : 3000;
    }

    @Input('longPressDisabled')
    disabled = false;

    @Input('longPressInterval')
    set _continuousInterval(v: number) {
        this.isContinuous = !!v;
        this.continuousInterval = v;
    }

    /** Threshold en ms para distinguir entre click y long press (default: 150ms) */
    @Input('longPressThreshold')
    pressThreshold = 150;

    private _duration = 3000;
    private isContinuous = false;
    private continuousInterval = 0;

    @Output() longPressStart = new EventEmitter<MouseEvent>();
    @Output() longPressFinish = new EventEmitter<MouseEvent>();
    @Output() longPressCancel = new EventEmitter<MouseEvent>();
    /** Emitido cuando es un click normal (tap rápido) */
    @Output() normalClick = new EventEmitter<MouseEvent>();

    private pressing = false;
    private longPressSubscription?: Subscription;
    private thresholdSubscription?: Subscription;
    private startTriggered = false;
    private lastEvent?: MouseEvent;

    @HostListener('pointerdown', ['$event'])
    onPress(event: MouseEvent) {
        if (this.disabled) {
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        this.pressing = true;
        this.startTriggered = false;
        this.lastEvent = event;

        // Esperar el threshold antes de considerar que es un long press
        this.thresholdSubscription = timer(this.pressThreshold).subscribe(() => {
            if (this.pressing) {
                this.startTriggered = true;
                this.longPressStart.emit(event);
                this.startLongPressTimer(event);
            }
        });
    }

    private startLongPressTimer(event: MouseEvent) {
        // La duración restante después del threshold
        const remainingDuration = this._duration - this.pressThreshold;

        let obs = timer(remainingDuration).pipe(mapTo(event));

        if (this.isContinuous) {
            obs = obs.pipe(
                tap((mouseEvent: MouseEvent) =>
                    this.longPressFinish.emit(mouseEvent),
                ),
                switchMap((mouseEvent) =>
                    interval(this.continuousInterval).pipe(mapTo(mouseEvent)),
                ),
                takeUntil(this.longPressCancel),
            );
        }

        this.longPressSubscription = obs.subscribe((mouseEvent) => {
            if (this.pressing) {
                this.pressing = this.isContinuous;
                this.longPressFinish.emit(mouseEvent);
            }
        });
    }

    @HostListener('pointerup', ['$event'])
    @HostListener('pointerleave', ['$event'])
    onRelease(event: MouseEvent) {
        const wasStartTriggered = this.startTriggered;

        this.pressing = false;

        // Cancelar el threshold timer si aún no se disparó
        if (this.thresholdSubscription) {
            this.thresholdSubscription.unsubscribe();
            this.thresholdSubscription = undefined;
        }

        if (this.longPressSubscription) {
            this.longPressSubscription.unsubscribe();
            this.longPressSubscription = undefined;
        }

        // Si nunca se disparó longPressStart, fue un click normal
        if (!wasStartTriggered && this.lastEvent) {
            this.normalClick.emit(this.lastEvent);
        } else {
            this.longPressCancel.emit(event);
        }

        this.startTriggered = false;
        this.lastEvent = undefined;
    }

    @HostListener('touchstart', ['$event'])
    handleTouchStart(event: TouchEvent) {
        // Convertir touch a mouse event y llamar a onPress
        const touch = event.touches[0];
        const mouseEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            target: event.target,
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation()
        } as MouseEvent;

        this.onPress(mouseEvent);
        event.preventDefault();
    }

    @HostListener('touchend', ['$event'])
    @HostListener('touchcancel', ['$event'])
    handleTouchEnd(event: TouchEvent) {
        const mouseEvent = {
            target: event.target,
            preventDefault: () => event.preventDefault(),
            stopPropagation: () => event.stopPropagation()
        } as MouseEvent;

        this.onRelease(mouseEvent);
    }
}
